import { useState } from 'react';
import {
  Card, CardHeader, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Button, Stack,
  TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Chip,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { EmptyState } from '../../../components/EmptyState';
import { handoversApi, type CreateHandoverInput } from '../../../api/production.api';
import { evcpdProductsApi } from '../../../api/evcpdProducts.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

const emptyForm: CreateHandoverInput = { productId: 0, warehouseId: 0, quantity: 0 };

export function StockHandoverPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateHandoverInput>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['stock-handovers'], queryFn: () => handoversApi.list({ limit: 50 }) });
  const products = useQuery({ queryKey: ['evcpd-products', 'handover-picker'], queryFn: () => evcpdProductsApi.list({ status: 'active', limit: 200 }), enabled: createOpen });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['stock-handovers'] });

  const createMut = useMutation({
    mutationFn: () => handoversApi.create(form),
    onSuccess: () => { setCreateOpen(false); setForm(emptyForm); setError(null); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const completeMut = useMutation({ mutationFn: (id: number) => handoversApi.complete(id), onSuccess: invalidate });

  const set = <K extends keyof CreateHandoverInput>(k: K, v: CreateHandoverInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <>
      <PageHeader
        title="Stock Handovers"
        subtitle="Hand produced units into inventory — completing a handover posts a goods receipt."
        action={hasPermission('stock_handover.create') && <Button variant="contained" onClick={() => { setForm(emptyForm); setError(null); setCreateOpen(true); }}>New Handover</Button>}
      />
      <Card>
        <CardHeader title="Handovers" titleTypographyProps={{ variant: 'subtitle1' }} />
        <CardContent sx={{ p: 0, overflowX: 'auto' }}>
          {(list.data?.data.length ?? 0) === 0 ? <EmptyState title="No handovers yet" dense /> : (
            <Table size="small">
              <TableHead><TableRow><TableCell>Code</TableCell><TableCell>Product</TableCell><TableCell>Warehouse</TableCell><TableCell align="right">Qty</TableCell><TableCell>Status</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {(list.data?.data ?? []).map((h) => (
                  <TableRow key={h.id} hover>
                    <TableCell>{h.handoverCode}</TableCell>
                    <TableCell>#{h.productId}</TableCell>
                    <TableCell>#{h.warehouseId}</TableCell>
                    <TableCell align="right">{h.quantity}</TableCell>
                    <TableCell><Chip size="small" label={h.status} color={h.status === 'completed' ? 'success' : 'default'} /></TableCell>
                    <TableCell align="right">
                      {h.status === 'pending' && hasPermission('stock_handover.complete') && (
                        <Button size="small" onClick={() => completeMut.mutate(h.id)}>Complete → Stock</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New Stock Handover</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select label="Product" value={form.productId || ''} onChange={(e) => set('productId', Number(e.target.value))} required fullWidth helperText="Product must be linked to an inventory item">
              {(products.data?.data ?? []).filter((p) => p.inventoryItemId).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField label="Warehouse ID" type="number" value={form.warehouseId || ''} onChange={(e) => set('warehouseId', Number(e.target.value))} required fullWidth />
              <TextField label="Quantity" type="number" value={form.quantity || ''} onChange={(e) => set('quantity', Number(e.target.value))} required fullWidth />
              <TextField label="Unit Cost (₹)" type="number" value={form.unitCost ?? ''} onChange={(e) => set('unitCost', e.target.value ? Number(e.target.value) : undefined)} fullWidth />
            </Stack>
            <TextField label="Received by" value={form.receivedBy ?? ''} onChange={(e) => set('receivedBy', e.target.value)} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.productId || !form.warehouseId || !form.quantity} onClick={() => createMut.mutate()}>Create</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
